import { useState } from 'react';
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { Sidebar } from '@/components/layout/Sidebar';
import { Navbar } from '@/components/layout/Navbar';
import { ContextGate } from '@/components/layout/ContextGate';
import { ReadinessGate } from '@/components/layout/ReadinessGate';
import { WorkspaceSwitchOverlay } from '@/components/layout/WorkspaceSwitchOverlay';
import { AppPageColumn } from '@/components/layout/AppPageColumn';
import { AskAgentWidget } from '@/components/ask';

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Browser live view is a fullscreen overlay — it must render outside of
  // ContextGate (which blocks the outlet until a workspace context resolves)
  // and outside the app shell (sidebar, navbar).
  const isBrowserLive = pathname.startsWith('/browser-live/');

  if (isBrowserLive) {
    return <Outlet />;
  }

  return (
    <div className="flex h-full bg-bg text-text">
      <WorkspaceSwitchOverlay />
      {/* Desktop sidebar */}
      <div className="sidebar-hide">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileNavOpen && (
        <div className="sidebar-overlay fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-text/10 backdrop-blur-[2px]" onClick={() => setMobileNavOpen(false)} />
          <div className="relative z-10 h-full">
            <Sidebar />
          </div>
        </div>
      )}

      <div className="flex-1 ml-0 min-[901px]:ml-[232px] flex flex-col min-h-0 overflow-x-hidden">
        <Navbar onMenuClick={() => setMobileNavOpen(!mobileNavOpen)} />
        {/*
          The page scroller is the FULL region under the navbar, with the page padding INSIDE it, so
          its scrollbar sits at the window's right edge — what `Page scroll="edge"` already does for
          the listings. It used to be the 1600px content column itself, inside <main>'s padding: its
          `overflow-x-auto` made it the vertical scroller too, so on any page taller than the window
          the scrollbar sat on the content's right edge, 32px in from the window's, with no gap.
          The column keeps `flex-1` (full-bleed pages still fill it) but not `min-h-0`, so it grows
          with its content and never scrolls itself.
        */}
        <main className="flex-1 flex flex-col min-h-0">
          <AppPageColumn>
            <ReadinessGate>
              <ContextGate>
                <Outlet />
              </ContextGate>
            </ReadinessGate>
          </AppPageColumn>
        </main>
      </div>
      <AskAgentWidget />
    </div>
  );
}
